/**
 * The live element demos, and the heights their frames reserve.
 *
 * Every demo declares its own `height`. The frame renders a skeleton of exactly that
 * height before the element mounts, so the page's layout is identical before and after
 * hydration — a demo that measured itself would shift the column under a reader the
 * moment it arrived, which is the one thing a documentation page must not do.
 *
 * The registry is a closed list rather than free-form props on the tag. An author names
 * a demo; the composition itself is TypeScript, so `tsc` checks it against the real
 * elements and the demo cannot drift from the package it demonstrates.
 */
export interface ElementDemo {
  /**
   * The file under `demos/` that draws this demo, and exports a component of the same name.
   *
   * One demo, one file, and the frame's Code tab shows that file — so the code a reader
   * copies is the code that just rendered above it, rather than a second copy of it kept in
   * a string somewhere. `demos.test.ts` holds the registry and the directory in bijection,
   * because a demo whose file is missing renders an error boundary and a file no demo names
   * is dead weight nobody notices.
   */
  readonly component: string;
  /** The frame's reserved height in pixels, and the skeleton's. */
  readonly height: number;
  /** The element this demo draws, for the frame's accessible name. */
  readonly label: string;
  /**
   * Whether this demo mounts a real map beside the element.
   *
   * A map demo is a different thing from every other one here, and the flag is what keeps the
   * difference honest. It brings the renderer, so it loads only when a reader asks for it — the
   * frame draws a poster with a button, and the chunk is fetched on the press. Nothing on a page
   * carrying one downloads a renderer for scrolling past it, which is what
   * `scripts/check-bundle.mjs` asserts of the emitted bytes rather than of this comment.
   */
  readonly withMap?: boolean;
}

export const ELEMENT_DEMOS = {
  "collab-presence": {
    component: "CollabPresence",
    height: 140,
    label: "a live collaboration session over a scripted transport",
  },
  "headless-notifications": {
    component: "HeadlessNotifications",
    height: 150,
    label: "the notification seam bound to a caller's own strip",
  },
  "headless-segment-list": {
    component: "HeadlessSegmentList",
    height: 250,
    label: "the studio hooks drawing a caller's own segment list",
  },
  "itinerary-profile": {
    component: "ItineraryProfileBasic",
    height: 200,
    label: "ItineraryProfile",
  },
  "itinerary-profile-full": {
    component: "ItineraryProfileFull",
    height: 320,
    label: "ItineraryProfile with its axis, sections and tooltip",
  },
  "itinerary-profile-playground": {
    component: "ItineraryProfilePlayground",
    height: 560,
    label: "ItineraryProfile playground",
  },
  "itinerary-profile-with-map": {
    component: "ItineraryProfileWithMap",
    height: 460,
    label: "ItineraryProfile beside a live map",
    withMap: true,
  },
  "itinerary-segment-table": {
    component: "ItinerarySegmentTableBasic",
    height: 420,
    label: "ItinerarySegmentTable",
  },
  "itinerary-segment-table-playground": {
    component: "ItinerarySegmentTablePlayground",
    height: 620,
    label: "ItinerarySegmentTable playground",
  },
  "itinerary-segment-table-with-map": {
    component: "ItinerarySegmentTableWithMap",
    height: 520,
    label: "ItinerarySegmentTable beside a live map",
    withMap: true,
  },
  "itinerary-waypoint-table": {
    component: "ItineraryWaypointTableBasic",
    height: 460,
    label: "ItineraryWaypointTable",
  },
  "itinerary-waypoint-table-course": {
    component: "ItineraryWaypointTableCourse",
    height: 560,
    label: "ItineraryWaypointTable as a race course page",
  },
  "itinerary-waypoint-table-playground": {
    component: "ItineraryWaypointTablePlayground",
    height: 660,
    label: "ItineraryWaypointTable playground",
  },
  "layers-panel": {
    component: "LayersPanelDemo",
    height: 500,
    label: "LayersPanel over a live map",
    withMap: true,
  },
  "map-controls": {
    component: "MapControlsShowcase",
    height: 420,
    label: "every map control over a live map",
    withMap: true,
  },
  "map-view": {
    component: "MapViewBasic",
    height: 340,
    label: "MapView",
    withMap: true,
  },
  "preferences-panel": {
    component: "PreferencesPanelDemo",
    height: 500,
    label: "PreferencesPanel over a live map",
    withMap: true,
  },
  "search-control": {
    component: "SearchControlDemo",
    height: 340,
    label: "SearchControl over a live map",
    withMap: true,
  },
  "segment-list": {
    component: "SegmentListBasic",
    height: 340,
    label: "SegmentList",
  },
  "segment-list-playground": {
    component: "SegmentListPlayground",
    height: 560,
    label: "SegmentList playground",
  },
  "street-view-control": {
    component: "StreetViewControlDemo",
    height: 340,
    label: "StreetViewControl over a live map",
    withMap: true,
  },
  "studio-chrome-with-map": {
    component: "StudioChromeWithMap",
    height: 520,
    label: "Studio chrome beside a live map",
    withMap: true,
  },
  "studio-dialog-chrome": {
    component: "StudioDialogChrome",
    height: 70,
    label: "AppModal, ModalFooter and ConfirmModal",
  },
  "studio-form-fields": {
    component: "StudioFormFields",
    height: 610,
    label: "the studio's own form controls",
  },
  "studio-full": {
    component: "FullStudio",
    height: 1190,
    label: "a full studio composed from the published packages, in a customer's brand",
    withMap: true,
  },
  "studio-panel-chrome": {
    component: "StudioPanelChrome",
    height: 350,
    label: "the chrome a sidenav panel is built from",
  },
  "studio-states": {
    component: "StudioStates",
    height: 705,
    label: "the loading, empty, failed and warning states",
  },
  "video-profile-overlay": {
    component: "VideoProfileOverlay",
    height: 520,
    label: "the three burnt-in elevation overlay designs",
  },
  "waypoint-card": {
    component: "WaypointCardBasic",
    height: 360,
    label: "WaypointCard",
  },
  "waypoint-card-headless": {
    component: "WaypointCardHeadless",
    height: 220,
    label: "useWaypointCard drawing a caller's own markup",
  },
  "waypoint-list": {
    component: "WaypointListBasic",
    height: 340,
    label: "WaypointList",
  },
} as const satisfies Record<string, ElementDemo>;

/** ElementDemoName is the closed set of demos an author may name. */
export type ElementDemoName = keyof typeof ELEMENT_DEMOS;

/** DEMO_NAMES is the same set as an array, for the tag schema's `matches` list. */
export const DEMO_NAMES = Object.keys(ELEMENT_DEMOS) as readonly ElementDemoName[];

/** isDemoName narrows an authored attribute to a registered demo. */
export const isDemoName = (value: string): value is ElementDemoName =>
  Object.hasOwn(ELEMENT_DEMOS, value);
